// =================================================================
// ProjectsTreeSubmenu.js — S44 hybride
// Arbre dépliable : projet > sessions groupées par bucket de date
// Complémentaire à RecentSessionsSubmenu (flat top-5 "reprendre")
// =================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, MessageCircle, MoreHorizontal, FolderOpen } from 'lucide-react';

const LS_KEY = 'itriia_expanded_projects_v1';
const LS_COLLAPSED = 'itriia_projects_tree_collapsed_v1';

// Groupage sessions par bucket de date (plus récent d'abord)
const bucketByDate = (sessions) => {
  const order = [];
  const map = new Map();
  const push = (k, s) => {
    if (!map.has(k)) { map.set(k, []); order.push(k); }
    map.get(k).push(s);
  };
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  const today = t0.getTime();
  const yesterday = today - 86_400_000;
  const weekAgo = today - 6 * 86_400_000;

  const sorted = [...sessions].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  for (const s of sorted) {
    const t = new Date(s.updated_at).getTime();
    let k;
    if (t >= today) k = "Aujourd'hui";
    else if (t >= yesterday) k = 'Hier';
    else if (t >= weekAgo) k = 'Cette semaine';
    else k = new Date(s.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    push(k, s);
  }
  return order.map(k => [k, map.get(k)]);
};

const loadExpanded = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]')); }
  catch { return new Set(); }
};

const persistExpanded = (set) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify([...set])); } catch { /* silent */ }
};

const ProjectsTreeSubmenu = ({ projects = [], onOpen, currentProjectId }) => {
  const [expanded, setExpanded] = useState(loadExpanded);
  const [sessionsByProject, setSessionsByProject] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [menuOpen, setMenuOpen] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_COLLAPSED) === '1'; } catch { return false; }
  });
  const menuRef = useRef(null);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_COLLAPSED, next ? '1' : '0'); } catch { /* silent */ }
      return next;
    });
  };

  const fetchSessions = useCallback(async (projectId) => {
    setLoadingMap(prev => ({ ...prev, [projectId]: true }));
    try {
      const resp = await axios.get(`/api/projects/${projectId}/sessions`);
      setSessionsByProject(prev => ({ ...prev, [projectId]: Array.isArray(resp.data) ? resp.data : [] }));
    } catch (e) {
      console.warn('projects-tree sessions load failed:', e?.response?.status || e.message);
    } finally {
      setLoadingMap(prev => ({ ...prev, [projectId]: false }));
    }
  }, []);

  // Auto-expand le projet actif (UX : tu vois où tu es)
  useEffect(() => {
    if (!currentProjectId) return;
    setExpanded(prev => {
      if (prev.has(currentProjectId)) return prev;
      const next = new Set(prev); next.add(currentProjectId);
      persistExpanded(next);
      return next;
    });
    if (!sessionsByProject[currentProjectId]) fetchSessions(currentProjectId);
  }, [currentProjectId, fetchSessions]);
  // Pré-charger les projets déjà dépliés au montage
  useEffect(() => {
    for (const pid of expanded) {
      if (!sessionsByProject[pid] && !loadingMap[pid]) fetchSessions(pid);
    }
  }, []);
  const toggleProject = (projectId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
        if (!sessionsByProject[projectId]) fetchSessions(projectId);
      }
      persistExpanded(next);
      return next;
    });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const handleRename = async (p, s) => {
    const newTitle = window.prompt('Nouveau titre de la discussion :', s.title || '');
    if (!newTitle || newTitle.trim() === (s.title || '').trim()) return;
    try {
      await axios.put(`/api/projects/${p.id}/sessions/${s.id}/rename`, { title: newTitle.trim() });
      fetchSessions(p.id);
    } catch { /* silent */ }
    setMenuOpen(null);
  };

  const handleArchive = async (p, s) => {
    try {
      await axios.put(`/api/projects/${p.id}/sessions/${s.id}/archive`);
      fetchSessions(p.id);
    } catch { /* silent */ }
    setMenuOpen(null);
  };

  if (!projects.length) return null;

  return (
    <div className="projects-tree-submenu">
      <style>{`
        .projects-tree-submenu {
          margin: 4px 8px 8px 16px;
          padding: 4px 0;
          border-left: 2px solid var(--card-border);
          background: var(--sub-items-bg);
          border-radius: 8px;
        }
        .pt-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--sidebar-section-label, #a5b4fc);
          padding: 8px 10px 6px;
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          border-radius: 6px;
          transition: background 0.15s ease;
        }
        .pt-header:hover {
          background: var(--sidebar-active-bg);
        }
        .pt-header-chevron {
          flex-shrink: 0;
          opacity: 0.75;
          transition: transform 0.15s ease;
        }
        .pt-header-chevron.open { transform: rotate(90deg); }
        .pt-project-btn {
          width: calc(100% - 12px);
          display: flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          margin: 1px 6px;
          padding: 5px 8px;
          border-radius: 6px;
          cursor: pointer;
          color: var(--sidebar-text);
          font-size: 13px;
          font-weight: 500;
          text-align: left;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .pt-project-btn:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .pt-project-btn.active {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .pt-chevron {
          flex-shrink: 0;
          opacity: 0.7;
          transition: transform 0.15s ease;
        }
        .pt-chevron.open { transform: rotate(90deg); }
        .pt-folder-icon {
          flex-shrink: 0;
          opacity: 0.85;
        }
        .pt-project-name {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pt-count {
          font-size: 10.5px;
          color: var(--text-muted, #6a6a85);
          font-weight: 400;
          flex-shrink: 0;
        }
        .pt-children {
          padding-left: 14px;
          margin-left: 10px;
          border-left: 1px dashed var(--card-border);
          margin-bottom: 4px;
        }
        .pt-bucket-header {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.3px;
          color: var(--text-muted, #6a6a85);
          padding: 6px 8px 2px;
        }
        .pt-session {
          position: relative;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          margin: 1px 4px;
          border-radius: 5px;
          cursor: pointer;
          font-size: 12px;
          color: var(--sidebar-text);
          transition: background 0.15s ease, color 0.15s ease;
        }
        .pt-session:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .pt-session-icon {
          flex-shrink: 0;
          opacity: 0.7;
        }
        .pt-session-title {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pt-session-actions {
          background: none;
          border: none;
          color: var(--text-muted, #6a6a85);
          padding: 2px;
          border-radius: 4px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.15s ease, background 0.15s ease;
          flex-shrink: 0;
        }
        .pt-session:hover .pt-session-actions { opacity: 1; }
        .pt-session-actions:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .pt-menu {
          position: absolute;
          right: 4px;
          top: 100%;
          margin-top: 2px;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          box-shadow: 0 6px 16px rgba(0,0,0,0.18);
          z-index: 50;
          min-width: 150px;
          padding: 4px;
        }
        .pt-menu-item {
          width: 100%;
          text-align: left;
          padding: 6px 10px;
          font-size: 12px;
          background: none;
          border: none;
          border-radius: 4px;
          color: var(--text-primary, var(--sidebar-text));
          cursor: pointer;
        }
        .pt-menu-item:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .pt-empty {
          padding: 4px 10px;
          font-size: 11.5px;
          color: var(--text-muted, #6a6a85);
          font-style: italic;
        }
      `}</style>

      <button
        type="button"
        className="pt-header"
        onClick={toggleCollapsed}
        title={collapsed ? 'Afficher Mes projets' : 'Masquer'}
      >
        <ChevronRight size={11} className={`pt-header-chevron ${!collapsed ? 'open' : ''}`} />
        <span>Mes projets</span>
      </button>

      {!collapsed && projects.map(p => {
        const isOpen = expanded.has(p.id);
        const sessions = sessionsByProject[p.id] || [];
        const loading = loadingMap[p.id];
        const buckets = isOpen ? bucketByDate(sessions) : [];
        const isActive = currentProjectId === p.id;
        return (
          <div key={p.id}>
            <button
              className={`pt-project-btn ${isActive ? 'active' : ''}`}
              onClick={() => toggleProject(p.id)}
              title={p.name}
            >
              <ChevronRight size={12} className={`pt-chevron ${isOpen ? 'open' : ''}`} />
              <FolderOpen size={13} className="pt-folder-icon" />
              <span className="pt-project-name">
                {p.project_icon ? p.project_icon + ' ' : ''}{p.name}
              </span>
              {!!p.conversation_count && (
                <span className="pt-count">{p.conversation_count}</span>
              )}
            </button>
            {isOpen && (
              <div className="pt-children">
                {loading && <div className="pt-empty">Chargement…</div>}
                {!loading && sessions.length === 0 && (
                  <div className="pt-empty">Aucune discussion</div>
                )}
                {buckets.map(([bucket, list]) => (
                  <div key={bucket}>
                    <div className="pt-bucket-header">{bucket}</div>
                    {list.map(s => {
                      const mKey = `${p.id}-${s.id}`;
                      return (
                        <div
                          key={s.id}
                          className="pt-session"
                          onClick={() => onOpen?.({ ...s, project_id: p.id, project_name: p.name })}
                          title={s.title || 'Nouvelle conversation'}
                          ref={menuOpen === mKey ? menuRef : null}
                        >
                          <MessageCircle size={11} className="pt-session-icon" />
                          <span className="pt-session-title">
                            {s.title || 'Nouvelle conversation'}
                          </span>
                          <button
                            className="pt-session-actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpen(menuOpen === mKey ? null : mKey);
                            }}
                            title="Actions"
                          >
                            <MoreHorizontal size={13} />
                          </button>
                          {menuOpen === mKey && (
                            <div className="pt-menu" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="pt-menu-item"
                                onClick={() => {
                                  onOpen?.({ ...s, project_id: p.id, project_name: p.name });
                                  setMenuOpen(null);
                                }}
                              >
                                Ouvrir
                              </button>
                              <button className="pt-menu-item" onClick={() => handleRename(p, s)}>Renommer</button>
                              <button className="pt-menu-item" onClick={() => handleArchive(p, s)}>Archiver</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProjectsTreeSubmenu;
